import React from 'react';
import { Card, CardContent } from "@/ui";
import { Shield, Lock, FileText, CheckCircle, Eye, Building, Activity, AlertTriangle, Users, Key, TrendingUp, Mail } from 'lucide-react';

const PRIMARY_BLUE = "#1356a6";

export default function Security() {
  return (
    <div
      className="min-h-screen font-sans"
      style={{
        backgroundImage: "url('/assets/shared/simple-bg.png')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundAttachment: "fixed",
        fontFamily: "Montserrat, system-ui, sans-serif",
      }}
    >
      <main className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-[84rem] mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="flex justify-center mb-6">
              <div className="p-4 rounded-full bg-blue-50 shadow-sm">
                <Shield className="h-8 w-8" style={{ color: PRIMARY_BLUE }} />
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl font-normal mb-4" style={{ color: PRIMARY_BLUE, fontFamily: "Montserrat, system-ui, sans-serif" }}>
              Security Practices
            </h1>
            <p className="text-lg text-gray-600" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
              <strong>Effective Date:</strong> January 8, 2026
            </p>
          </div>

          {/* Introduction */}
          <Card className="border-2 shadow-lg mb-8">
            <CardContent className="p-8">
              <p className="text-base leading-relaxed text-gray-700" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                AIscendus Inc ("AIscendus") is committed to protecting the confidentiality, integrity, and availability of customer data. We maintain a comprehensive information security program designed to safeguard data processed through our website, software platforms, and services.
              </p>
            </CardContent>
          </Card>

          {/* Section 1: Security Program Overview */}
          <Card className="border-2 shadow-lg mb-8">
            <CardContent className="p-8">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b-2">
                <div className="p-2 rounded-lg bg-blue-50 shadow-sm">
                  <Shield className="h-6 w-6" style={{ color: PRIMARY_BLUE }} />
                </div>
                <h2 className="text-2xl font-semibold" style={{ color: PRIMARY_BLUE, fontFamily: "Montserrat, system-ui, sans-serif" }}>
                  1. Security Program Overview
                </h2>
              </div>
              <p className="text-base leading-relaxed text-gray-700 mb-3" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                AIscendus maintains an organization-wide information security program that includes administrative, technical, and physical safeguards appropriate to the nature of the data processed and the risks presented.
              </p>
              <p className="text-base leading-relaxed text-gray-700 mb-3" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                Our security practices are designed to:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                <li>Prevent unauthorized access, disclosure, or alteration of data</li>
                <li>Protect against anticipated threats or hazards</li>
                <li>Ensure system reliability and availability</li>
              </ul>
            </CardContent>
          </Card>

          {/* Section 2: Alignment with Industry Standards */}
          <Card className="border-2 shadow-lg mb-8">
            <CardContent className="p-8">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b-2">
                <div className="p-2 rounded-lg bg-blue-50 shadow-sm">
                  <CheckCircle className="h-6 w-6" style={{ color: PRIMARY_BLUE }} />
                </div>
                <h2 className="text-2xl font-semibold" style={{ color: PRIMARY_BLUE, fontFamily: "Montserrat, system-ui, sans-serif" }}>
                  2. Alignment with Industry Standards
                </h2>
              </div>
              <p className="text-base leading-relaxed text-gray-700 mb-3" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                AIscendus' security program is aligned with recognized industry frameworks and standards, including:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4 mb-3" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                <li><strong>SOC 2 Trust Services Criteria</strong> (Security, Availability, Confidentiality)</li>
                <li><strong>ISO/IEC 27001 Information Security Management System (ISMS)</strong> principles</li>
                <li><strong>HIPAA Security Rule</strong> requirements (where applicable)</li>
              </ul>
              <p className="text-base leading-relaxed text-gray-700" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                Alignment does not imply certification unless expressly stated in a written agreement.
              </p>
            </CardContent>
          </Card>

          {/* Section 3: Administrative Safeguards */}
          <Card className="border-2 shadow-lg mb-8">
            <CardContent className="p-8">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b-2">
                <div className="p-2 rounded-lg bg-blue-50 shadow-sm">
                  <FileText className="h-6 w-6" style={{ color: PRIMARY_BLUE }} />
                </div>
                <h2 className="text-2xl font-semibold" style={{ color: PRIMARY_BLUE, fontFamily: "Montserrat, system-ui, sans-serif" }}>
                  3. Administrative Safeguards
                </h2>
              </div>
              <p className="text-base leading-relaxed text-gray-700 mb-3" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                AIscendus implements administrative safeguards, including:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4 mb-3" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                <li>Written information security policies and procedures</li>
                <li>Defined security roles and responsibilities</li>
                <li>Workforce security training and awareness programs</li>
                <li>Risk assessment and risk management processes</li>
                <li>Incident response and breach management procedures</li>
              </ul>
              <p className="text-base leading-relaxed text-gray-700" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                All personnel are required to adhere to confidentiality and security obligations.
              </p>
            </CardContent>
          </Card>

          {/* Section 4: Technical Safeguards */}
          <Card className="border-2 shadow-lg mb-8">
            <CardContent className="p-8">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b-2">
                <div className="p-2 rounded-lg bg-blue-50 shadow-sm">
                  <Lock className="h-6 w-6" style={{ color: PRIMARY_BLUE }} />
                </div>
                <h2 className="text-2xl font-semibold" style={{ color: PRIMARY_BLUE, fontFamily: "Montserrat, system-ui, sans-serif" }}>
                  4. Technical Safeguards
                </h2>
              </div>
              <p className="text-base leading-relaxed text-gray-700 mb-3" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                Technical safeguards used to protect systems and data include:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4 mb-3" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                <li>Encryption of data in transit and at rest</li>
                <li>Role-based access controls and least-privilege principles</li>
                <li>Multi-factor authentication where appropriate</li>
                <li>Audit logging and monitoring of system activity</li>
                <li>Automatic session timeouts and access controls</li>
              </ul>
              <p className="text-base leading-relaxed text-gray-700" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                Access to systems is limited to authorized users based on job responsibilities.
              </p>
            </CardContent>
          </Card>

          {/* Section 5: Physical Safeguards */}
          <Card className="border-2 shadow-lg mb-8">
            <CardContent className="p-8">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b-2">
                <div className="p-2 rounded-lg bg-blue-50 shadow-sm">
                  <Building className="h-6 w-6" style={{ color: PRIMARY_BLUE }} />
                </div>
                <h2 className="text-2xl font-semibold" style={{ color: PRIMARY_BLUE, fontFamily: "Montserrat, system-ui, sans-serif" }}>
                  5. Physical Safeguards
                </h2>
              </div>
              <p className="text-base leading-relaxed text-gray-700 mb-3" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                AIscendus implements physical safeguards to protect systems and infrastructure, including:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                <li>Controlled access to facilities and equipment</li>
                <li>Secure data center environments</li>
                <li>Device and media management procedures</li>
                <li>Protections against unauthorized physical access</li>
              </ul>
            </CardContent>
          </Card>

          {/* Section 6: Monitoring and Testing */}
          <Card className="border-2 shadow-lg mb-8">
            <CardContent className="p-8">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b-2">
                <div className="p-2 rounded-lg bg-blue-50 shadow-sm">
                  <Activity className="h-6 w-6" style={{ color: PRIMARY_BLUE }} />
                </div>
                <h2 className="text-2xl font-semibold" style={{ color: PRIMARY_BLUE, fontFamily: "Montserrat, system-ui, sans-serif" }}>
                  6. Monitoring and Testing
                </h2>
              </div>
              <p className="text-base leading-relaxed text-gray-700 mb-3" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                AIscendus regularly evaluates the effectiveness of its security controls through:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4 mb-3" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                <li>Continuous monitoring and logging</li>
                <li>Vulnerability assessments</li>
                <li>Security testing and reviews</li>
                <li>Periodic policy and control updates</li>
              </ul>
              <p className="text-base leading-relaxed text-gray-700" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                Identified risks are addressed in accordance with internal risk management processes.
              </p>
            </CardContent>
          </Card>

          {/* Section 7: Incident Response and Breach Notification */}
          <Card className="border-2 shadow-lg mb-8">
            <CardContent className="p-8">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b-2">
                <div className="p-2 rounded-lg bg-blue-50 shadow-sm">
                  <AlertTriangle className="h-6 w-6" style={{ color: PRIMARY_BLUE }} />
                </div>
                <h2 className="text-2xl font-semibold" style={{ color: PRIMARY_BLUE, fontFamily: "Montserrat, system-ui, sans-serif" }}>
                  7. Incident Response and Breach Notification
                </h2>
              </div>
              <p className="text-base leading-relaxed text-gray-700 mb-3" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                AIscendus maintains documented incident response procedures designed to:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4 mb-3" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                <li>Detect and investigate security incidents</li>
                <li>Mitigate potential harm</li>
                <li>Restore normal operations promptly</li>
              </ul>
              <p className="text-base leading-relaxed text-gray-700" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                Where required by law or contract, AIscendus provides breach notifications to customers without unreasonable delay.
              </p>
            </CardContent>
          </Card>

          {/* Section 8: Subcontractors and Third Parties */}
          <Card className="border-2 shadow-lg mb-8">
            <CardContent className="p-8">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b-2">
                <div className="p-2 rounded-lg bg-blue-50 shadow-sm">
                  <Users className="h-6 w-6" style={{ color: PRIMARY_BLUE }} />
                </div>
                <h2 className="text-2xl font-semibold" style={{ color: PRIMARY_BLUE, fontFamily: "Montserrat, system-ui, sans-serif" }}>
                  8. Subcontractors and Third Parties
                </h2>
              </div>
              <p className="text-base leading-relaxed text-gray-700 mb-3" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                AIscendus may engage third-party service providers to support service delivery. All such providers are:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                <li>Subject to written agreements</li>
                <li>Required to implement appropriate security safeguards</li>
                <li>Bound by confidentiality and data protection obligations</li>
              </ul>
            </CardContent>
          </Card>

          {/* Section 9: Customer Responsibilities */}
          <Card className="border-2 shadow-lg mb-8">
            <CardContent className="p-8">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b-2">
                <div className="p-2 rounded-lg bg-blue-50 shadow-sm">
                  <Key className="h-6 w-6" style={{ color: PRIMARY_BLUE }} />
                </div>
                <h2 className="text-2xl font-semibold" style={{ color: PRIMARY_BLUE, fontFamily: "Montserrat, system-ui, sans-serif" }}>
                  9. Customer Responsibilities
                </h2>
              </div>
              <p className="text-base leading-relaxed text-gray-700 mb-3" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                Customers are responsible for:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                <li>Maintaining the security of their user credentials</li>
                <li>Configuring access controls appropriately</li>
                <li>Complying with applicable laws and regulations</li>
                <li>Ensuring the accuracy and legality of data entered into the Services</li>
              </ul>
            </CardContent>
          </Card>

          {/* Section 10: Continuous Improvement */}
          <Card className="border-2 shadow-lg mb-8">
            <CardContent className="p-8">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b-2">
                <div className="p-2 rounded-lg bg-blue-50 shadow-sm">
                  <TrendingUp className="h-6 w-6" style={{ color: PRIMARY_BLUE }} />
                </div>
                <h2 className="text-2xl font-semibold" style={{ color: PRIMARY_BLUE, fontFamily: "Montserrat, system-ui, sans-serif" }}>
                  10. Continuous Improvement
                </h2>
              </div>
              <p className="text-base leading-relaxed text-gray-700" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                AIscendus continually reviews and enhances its security practices to address evolving threats, technology changes, and regulatory requirements.
              </p>
            </CardContent>
          </Card>

          {/* Section 11: Contact Information */}
          <Card className="border-2 shadow-lg mb-8">
            <CardContent className="p-8">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b-2">
                <div className="p-2 rounded-lg bg-blue-50 shadow-sm">
                  <Mail className="h-6 w-6" style={{ color: PRIMARY_BLUE }} />
                </div>
                <h2 className="text-2xl font-semibold" style={{ color: PRIMARY_BLUE, fontFamily: "Montserrat, system-ui, sans-serif" }}>
                  11. Contact Information
                </h2>
              </div>
              <p className="text-base leading-relaxed text-gray-700 mb-3" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                For questions regarding AIscendus' security practices, please contact:
              </p>
              <div className="space-y-2" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                <p className="text-base font-semibold text-gray-900">
                  <strong>AIscendus Inc</strong>
                </p>
                <p className="text-base text-gray-700">
                  Email: <a href="mailto:info@aiscendus.ai" className="text-blue-600 hover:underline">info@aiscendus.ai</a>
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}

